import useCustomSimplifiedForm from 'components/common/form/hooks/useCustomSimplifiedForm';
import useBundleTranslation from 'i18n';
import { ActSubmit } from 'components/common/form/act';
import React, { useEffect, useRef, useState } from 'react';
import { FormRendererProps } from 'components/common/form';
import { TemplateFormComponent } from 'components/common/form/layout/ReactHookFormController';
import { Stack } from '@mui/material';
import { createPortal } from 'react-dom';
import { Popup, PopupSettings } from 'components/common/popup/Popup';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';
import pollingService from 'tools/pollingService';
import { useConfigConstantValue } from 'hooks/useConfigConstant';

export default function ShareElementForm({
    elementId,
    segmentValueId,
    filters,
    isOpen,
    setIsOpen,
}: {
    elementId: number;
    segmentValueId: number;
    filters?: string;
    isOpen: boolean;
    setIsOpen: (state: boolean) => void;
}) {
    const hideUserEmail = useConfigConstantValue('HIDE_USER_EMAIL_ADDRESSES');

    const hookForm = useCustomSimplifiedForm(
        {
            filters: filters,
        },
        {
            uid: 'shareElementForm',
            isPopup: true,
            settingsUrl: `/data/element/share/${elementId}/${segmentValueId}/form`,
        } as FormRendererProps,
    );

    useEffect(() => {
        hookForm.form.hookFormSetValue('filters', filters);
    }, [filters]);

    const [pollingRunning, setPollingRunning] = useState(false);
    const { t } = useBundleTranslation();
    hookForm.form.formAct.overrideAct(
        'submit',
        class extends ActSubmit {
            exec = async (data: any) =>
                new Promise((resolve) =>
                    super.exec(data).then((response) => {
                        const { run } = pollingService({
                            onSuccess: async (data) => {
                                setPollingRunning(false);
                                alert(t('your_content_shared'));
                                setIsOpen(false);
                                resolve(data);
                            },
                            onError: (data) => {
                                setPollingRunning(false);
                                resolve(data);
                            },
                            requestData: {
                                poolingCreateURL: hookForm.form.settingsUrl,
                                poolingCreateMethod: 'POST',
                            },
                        });

                        if (response.data.status == 'QUEUE') {
                            setPollingRunning(true);
                            run(response.data.data.ppl_id);
                        } else {
                            resolve(response);
                        }
                    }),
                );
        },
    );

    const submit = useRef(
        hookForm.form.hookFormHandleSubmit(function (data) {
            return hookForm.form.formAct.act.submit.exec(data);
        }),
    );

    const popupSettings: PopupSettings = { title: t('share_now'), textOK: t('share') };
    return (
        <Popup
            settings={popupSettings}
            open={isOpen}
            onConfirm={() => {
                hookForm.form.formAct.act.submit.exec(hookForm.form.hookFormGetValues());
            }}
            onHide={() => setIsOpen(false)}
        >
            <form onSubmit={submit.current}>
                {createPortal(
                    <LoadingPlaceholder
                        open={hookForm.state.loadingMaskShow || pollingRunning}
                        sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
                    >
                        <>{hookForm.state.loadingMaskText}</>
                    </LoadingPlaceholder>,
                    document.body,
                )}
                <Stack className={'form-components-indent'}>
                    <TemplateFormComponent
                        wrapper
                        elementProps={hookForm}
                        component={'CheckBoxList'}
                        name={'emails'}
                        props={{
                            async: true,
                            label: 'share_with',
                            settingsUrl:
                                '/data/notification/favorites/get-users-settings?elementId=:elementId&segmentId=:segmentId',
                            dataUrl:
                                '/data/notification/favorites/get-users-data?elementId=:elementId&segmentId=:segmentId',
                            dataPageSize: 100,
                            showCounter: true,
                            selectAllLabel: 'users',
                            defaultRowHeight: hideUserEmail ? 32 : 50,
                            urlParams: {
                                elementId: elementId,
                                segmentId: segmentValueId,
                            },
                        }}
                    />
                    <TemplateFormComponent
                        wrapper
                        elementProps={hookForm}
                        component={'FormTextarea'}
                        name={'message'}
                        props={{
                            label: 'add_a_comment',
                        }}
                    />
                    <TemplateFormComponent
                        wrapper
                        elementProps={hookForm}
                        component={'EmbedCodePopup'}
                        name={'embed'}
                        props={{
                            type: 'element',
                            id: elementId,
                            elementType: 'element',
                        }}
                    />
                </Stack>
            </form>
        </Popup>
    );
}
